import React, { createContext, useContext, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { isEqual } from "lodash";
import useFilters, { FilterOptions } from "../../hooks/useFilters";
import {
  convertToQueryString,
  decodeQueryString,
  getCanonicalFilterOptions,
  normalizeFilterOptionsFromUrl,
} from "../../utils/transformUrl";

const FILTER_QUERY_KEYS = ["filter_filters", "filter_order", "page", "limit"];

const buildMergedFilterQueryString = (currentSearch: string, filters: FilterOptions) => {
  const mergedParams = new URLSearchParams(currentSearch);

  FILTER_QUERY_KEYS.forEach((key) => {
    mergedParams.delete(key);
  });

  const filterQueryString = convertToQueryString(filters);
  const filterParams = new URLSearchParams(filterQueryString);

  filterParams.forEach((value, key) => {
    mergedParams.set(key, value);
  });

  return mergedParams.toString();
};

const hasFilterParamsInSearch = (search: string) => {
  const params = new URLSearchParams(search);
  return FILTER_QUERY_KEYS.some((key) => params.has(key));
};

interface FiltersProviderProps {
    children: React.ReactNode;
    defaultFilterFilters?: any;
    syncToUrl?: boolean;
}

interface FiltersContextProps {
  filters: FilterOptions;
  updateFilters: any;
  updateFilterOrder: any;
  updatePage: any;
  updatePageSize: any;
  resetFilters: any;
  checkIfUrlHasFilters: any;
  setPage: any;
}

const FiltersContext = createContext<FiltersContextProps | undefined>(undefined);

export const FiltersProvider: React.FC<FiltersProviderProps> = ({ children , defaultFilterFilters, syncToUrl = true }) => {

  const navigate = useNavigate();
  const location = useLocation();
  const decodedFilters = decodeQueryString(location.search); // DECODED FILTERS FROM URL

   // DEFAULT VALUES
   const defaultFilterOrder: any[] = [];
   const defaultLimit = 50;
 
   // OBTAING IF DECODED FILTERS FILTERS EXIST
   const decodedFiltersExist = decodedFilters && decodedFilters.filter_filters && Object.keys(decodedFilters.filter_filters).length > 0;
 
   // CONFIGURE FILTERS
   const configuredFilters = decodedFiltersExist ? decodedFilters.filter_filters : defaultFilterFilters ? defaultFilterFilters : {};
   const configuredOrder = decodedFilters?.filter_order ?? defaultFilterOrder;
   const configuredLimit = decodedFilters?.limit ?? defaultLimit;
   const configuredPage = decodedFilters?.page ?? 1;
 
   // FILTERS HOOK
   const { filters, updateFilters, resetFilters, updateFilterOrder, updatePage, updatePageSize, setPage } = useFilters(
     configuredFilters,
     configuredOrder,
     configuredPage,
     configuredLimit,
     defaultFilterFilters
   );

   const lastSyncedQueryRef = useRef<string | null>(null);

   useEffect(() => {
       if (!syncToUrl) {
         return;
       }

       const queryString = buildMergedFilterQueryString(location.search, filters);
       const currentSearch = location.search.startsWith('?') ? location.search.slice(1) : location.search;

       if (queryString === currentSearch) {
         lastSyncedQueryRef.current = queryString;
         return;
       }

       const canonicalState = getCanonicalFilterOptions(filters);
       const canonicalUrl = normalizeFilterOptionsFromUrl(location.search);

       // Solo considerar sincronizado si la URL ya tiene parámetros y coincide semánticamente.
       // Si la URL está vacía, escribir el estado inicial (filtros por defecto, page, limit...).
       if (currentSearch && isEqual(canonicalState, canonicalUrl)) {
         lastSyncedQueryRef.current = queryString;
         return;
       }

       if (lastSyncedQueryRef.current === queryString) {
         return;
       }

       lastSyncedQueryRef.current = queryString;
       navigate(
         {
           pathname: location.pathname,
           search: `?${queryString}`,
         },
         {
           replace: true,
           state: location.state,
         }
       );
       // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, location.search]);

  const checkIfUrlHasFilters = () => {
    const urlHasFilters = hasFilterParamsInSearch(location.search);
    navigate(
      urlHasFilters
        ? {
            pathname: location.pathname,
            search: location.search,
          }
        : {
            pathname: location.pathname,
            search: `?${buildMergedFilterQueryString(location.search, filters)}`,
          },
      {
        replace: true,
        state: location.state,
      }
    );
  }

  const contextValue: FiltersContextProps = {
    filters,
    updateFilters,
    updateFilterOrder,
    updatePage,
    updatePageSize,
    resetFilters,
    checkIfUrlHasFilters,
    setPage,
  };

  return (
    <FiltersContext.Provider value={contextValue}>
      {children}
    </FiltersContext.Provider>
  );
};

export const useFiltersPR = (): FiltersContextProps => {
  const context = useContext(FiltersContext);
  if (!context) {
    throw new Error("useFiltersPR must be used within a FiltersProvider");
  }
  return context;
};
